import type { CommandHandler } from '../types';
import type { PayrollDeliveryResponse } from '../../types/payroll';

// --- Validation ---

type ValidationError = { success: false; error: string };
type ValidationOk = {
  success: true;
  salaryCalculationLineIds: number[];
  templateId: string;
  rowCount: number;
};
type ValidationResult = ValidationError | ValidationOk;

function validatePayrollContext(context: Parameters<CommandHandler>[1]): ValidationResult {
  const { selectedRows, requestConfirmation, generateAndSendPayroll, commands } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return { success: false, error: 'Please choose at least one row' };
  }

  if (!requestConfirmation) {
    return { success: false, error: 'Confirmation dialog is not available' };
  }

  if (!generateAndSendPayroll) {
    return { success: false, error: 'Payroll service is not available' };
  }

  const templateId = commands?.find((c) => c.alias === 'zprintform')?.templateSelection as
    string | undefined;

  if (!templateId) {
    return { success: false, error: 'Payroll template is not configured.' };
  }

  const salaryCalculationLineIds = Array.from(selectedRows)
    .filter((id) => id && !id.startsWith('temp_'))
    .map(Number)
    .filter(Number.isFinite);

  if (salaryCalculationLineIds.length === 0) {
    return { success: false, error: 'No saved rows selected to send' };
  }

  return {
    success: true,
    salaryCalculationLineIds,
    templateId,
    rowCount: salaryCalculationLineIds.length,
  };
}

// --- Result message builder ---

function buildResultMessage(result: PayrollDeliveryResponse): string {
  const skippedMsg = result.skippedNames?.length
    ? `\n\nMissing Email (Skipped):\n${result.skippedNames.map((n) => `• ${n}`).join('\n')}`
    : '';

  const failedMsg = result.failedNames?.length
    ? `\n\nErrors (Failed):\n${result.failedNames.map((n) => `• ${n}`).join('\n')}`
    : '';

  return `Sent: ${result.sent} | Skipped: ${result.skipped} | Failed: ${result.failed}${skippedMsg}${failedMsg}`;
}

// --- Handler ---

export const printFormAndSendEmailHandler: CommandHandler = async (_command, context) => {
  const { clearSelection, requestConfirmation, generateAndSendPayroll } = context;

  const validation = validatePayrollContext(context);

  if (!validation.success) return validation;

  const { salaryCalculationLineIds, templateId, rowCount } = validation;

  const confirmed = await requestConfirmation!(`Send ${rowCount} payroll document(s) by email?`);
  if (!confirmed) return { success: false };

  try {
    const result = await generateAndSendPayroll!({ salaryCalculationLineIds, templateId });
    return { success: true, message: buildResultMessage(result), shouldRefresh: true };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send payroll documents',
    };
  } finally {
    clearSelection?.();
  }
};
